import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { feeManagementAPI, groupAPI } from '../services/api';

const PendingFeesReminder = () => {
  const navigate = useNavigate();
  const [allFees, setAllFees] = useState([]);
  const [filteredFees, setFilteredFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchGroups, setBatchGroups] = useState([]);
  const [maxInstallments, setMaxInstallments] = useState(0);
  
  // Filter states
  const [groupFilter, setGroupFilter] = useState('all');
  const [installmentFilter, setInstallmentFilter] = useState('all'); // 'all', '1', '2', '3'
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'overdue', 'today', 'custom'
  const [customDays, setCustomDays] = useState(7);
  const [dateDirection, setDateDirection] = useState('before'); // 'before' or 'after'
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Sending states
  const [sendingReminder, setSendingReminder] = useState(false);

  useEffect(() => {
    fetchBatchGroups();
    fetchAllFees();
  }, []);

  useEffect(() => {
    fetchAllFees();
  }, [groupFilter]);

  useEffect(() => {
    applyFilters();
  }, [allFees, installmentFilter, dateFilter, customDays, dateDirection]);

  useEffect(() => {
    // Calculate max installments from current allFees (filtered by group)
    const maxInst = allFees.reduce((max, fee) => {
      if (fee.paymentType === 'installments' && fee.installments) {
        const feeMaxInstallment = Math.max(...fee.installments.map(inst => inst.installmentNumber || 0));
        return Math.max(max, feeMaxInstallment);
      }
      return max;
    }, 0);
    setMaxInstallments(maxInst);
  }, [allFees]);

  useEffect(() => {
    // When filtered fees change, remove any selected IDs that are no longer in the filtered list
    const filteredIds = filteredFees.map(fee => fee._id);
    setSelectedIds(prev => {
      const filtered = prev.filter(id => filteredIds.includes(id));
      // Update selectAll state based on filtered selection
      if (filteredIds.length > 0 && filtered.length === filteredIds.length) {
        setSelectAll(true);
      } else if (filtered.length === 0 || filtered.length < filteredIds.length) {
        setSelectAll(false);
      }
      return filtered;
    });
  }, [filteredFees]);

  const fetchBatchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      // Filter only groups where isBatch is true
      const batches = response.data.groups.filter(group => group.isBatch === true);
      setBatchGroups(batches);
    } catch (error) {
      console.error('Error fetching batch groups:', error);
    }
  };

  const fetchAllFees = async () => {
    try {
      setLoading(true);
      const params = { status: 'all', limit: 1000 };
      if (groupFilter !== 'all') {
        params.groupId = groupFilter;
      }
      const response = await feeManagementAPI.getAllFees(params);
      
      // Filter to only show fees with pending or overdue installments
      const feesWithPending = response.data.feeManagements.filter(fee => {
        if (fee.paymentType === 'installments') {
          return fee.installments.some(inst => inst.status === 'pending' || inst.status === 'overdue');
        }
        return fee.status !== 'paid';
      });
      
      setAllFees(feesWithPending);
    } catch (error) {
      console.error('Error fetching fees:', error);
      alert('Failed to fetch fee records');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allFees];

    // Installment filter
    if (installmentFilter !== 'all') {
      const installmentNum = parseInt(installmentFilter);
      filtered = filtered.filter(fee => {
        if (fee.paymentType === 'installments') {
          return fee.installments.some(
            inst => inst.installmentNumber === installmentNum && (inst.status === 'pending' || inst.status === 'overdue')
          );
        }
        return false;
      });
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter(fee => {
        if (fee.paymentType === 'installments') {
          return fee.installments.some(inst => {
            if (inst.status !== 'pending' && inst.status !== 'overdue') return false;
            
            const dueDate = new Date(inst.dueDate);
            dueDate.setHours(0, 0, 0, 0);

            if (dateFilter === 'overdue') {
              return dueDate < today;
            } else if (dateFilter === 'today') {
              return dueDate.getTime() === today.getTime();
            } else if (dateFilter === 'custom') {
              const targetDate = new Date(today);
              
              if (dateDirection === 'before') {
                targetDate.setDate(targetDate.getDate() - customDays);
                return dueDate >= targetDate && dueDate <= today;
              } else {
                targetDate.setDate(targetDate.getDate() + customDays);
                return dueDate > today && dueDate <= targetDate;
              }
            }
            return false;
          });
        }
        return false;
      });
    }

    setFilteredFees(filtered);
  };

  const handleSelectToggle = (feeId) => {
    setSelectedIds(prev => {
      if (prev.includes(feeId)) {
        setSelectAll(false);
        return prev.filter(id => id !== feeId);
      } else {
        const newSelected = [...prev, feeId];
        if (newSelected.length === filteredFees.length) {
          setSelectAll(true);
        }
        return newSelected;
      }
    });
  };

  const handleSendBulkReminders = async () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one student');
      return;
    }

    if (!window.confirm(`Send reminders to ${selectedIds.length} selected student(s)?`)) {
      return;
    }

    setSendingReminder(true);
    
    try {
      const selectedFees = filteredFees.filter(fee => selectedIds.includes(fee._id));
      const results = [];

      for (const fee of selectedFees) {
        if (!fee.user) {
          results.push({
            name: 'Unknown',
            success: false,
            error: 'User not found'
          });
          continue;
        }

        if (!fee.user.email) {
          results.push({
            name: fee.user.name,
            success: false,
            error: 'No email address'
          });
          continue;
        }

        // Find pending installments based on current filter
        const pendingInstallments = getPendingInstallments(fee);
        
        if (pendingInstallments.length === 0) {
          results.push({
            name: fee.user.name,
            success: false,
            error: 'No pending installments'
          });
          continue;
        }

        // Send reminder for each filtered installment
        let allSent = true;
        let lastError = null;
        
        for (const inst of pendingInstallments) {
          try {
            await feeManagementAPI.sendInstallmentReminder(fee._id, inst.installmentNumber);
          } catch (error) {
            allSent = false;
            lastError = error.response?.data?.message || 'Failed to send';
            break;
          }
        }

        if (allSent) {
          results.push({
            name: fee.user.name,
            success: true
          });
        } else {
          results.push({
            name: fee.user.name,
            success: false,
            error: lastError
          });
        }
      }

      // Show results
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      let message = `✅ Successfully sent: ${successCount}\n`;
      if (failCount > 0) {
        message += `❌ Failed: ${failCount}\n\nFailed:\n`;
        results.filter(r => !r.success).forEach(r => {
          message += `- ${r.name}: ${r.error}\n`;
        });
      }

      alert(message);
      
      // Reset selections
      setSelectedIds([]);
      setSelectAll(false);
      
    } catch (error) {
      console.error('Error sending bulk reminders:', error);
      alert('Error sending reminders');
    } finally {
      setSendingReminder(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntil = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPendingInstallments = (fee) => {
    if (fee.paymentType !== 'installments') return [];
    let pending = fee.installments.filter(inst => inst.status === 'pending' || inst.status === 'overdue');
    
    // If specific installment is selected, show only that installment
    if (installmentFilter !== 'all') {
      const filterNum = parseInt(installmentFilter);
      pending = pending.filter(inst => inst.installmentNumber === filterNum);
    }
    
    return pending;
  };

  const getFilteredPendingAmount = (fee) => {
    if (fee.paymentType !== 'installments') return fee.pendingAmount;
    
    const pendingInst = getPendingInstallments(fee);
    return pendingInst.reduce((sum, inst) => sum + inst.amount, 0);
  };

  const getInstallmentStatus = (fee) => {
    const pending = getPendingInstallments(fee);
    if (pending.length === 0) return { text: 'All Paid', color: '#48bb78' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const overdue = pending.filter(inst => {
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    });
    
    if (overdue.length > 0) {
      return { text: `${overdue.length} Overdue`, color: '#f56565' };
    }
    
    return { text: `${pending.length} Pending`, color: '#fbbf24' };
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1600px', margin: '20px auto', background: '#ffffff', minHeight: 'calc(100vh - 100px)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', color: '#1a202c' }}>
            ⏰ Fee Reminders
          </h1>
          <p style={{ margin: 0, color: '#718096' }}>
            Filter and send payment reminders to students
          </p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button
            onClick={() => navigate('/admin/fee-dashboard')}
            style={{
              padding: '12px 24px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📊 Dashboard
          </button>
          <button
            onClick={() => navigate('/admin/fee-management')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Manage Fees
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: '#f7fafc',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '25px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '15px', fontWeight: '600', color: '#2d3748', fontSize: '16px' }}>
          🔍 Filters
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {/* Group/Batch Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#4a5568' }}>
              🎓 Batch/Group
            </label>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="all">All Batches</option>
              {batchGroups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.students.length} students)
                </option>
              ))}
            </select>
          </div>

          {/* Installment Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#4a5568' }}>
              Installment Number {maxInstallments > 0 && (
                <span style={{ fontSize: '12px', color: '#718096', fontWeight: '400' }}>
                  (Max: {maxInstallments})
                </span>
              )}
            </label>
            <select
              value={installmentFilter}
              onChange={(e) => setInstallmentFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="all">All Installments</option>
              {maxInstallments === 0 ? (
                <option disabled>No installments found</option>
              ) : (
                [...Array(maxInstallments)].map((_, index) => (
                  <option key={index + 1} value={index + 1}>
                    Installment {index + 1}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#4a5568' }}>
              Due Date Filter
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="all">All Dates</option>
              <option value="overdue">Overdue (Before Today)</option>
              <option value="today">Due Today</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#4a5568' }}>
                  Number of Days
                </label>
                <input
                  type="number"
                  value={customDays}
                  onChange={(e) => setCustomDays(parseInt(e.target.value) || 0)}
                  min="1"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#4a5568' }}>
                  Direction
                </label>
                <select
                  value={dateDirection}
                  onChange={(e) => setDateDirection(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  <option value="before">Before/Until Today (Past {customDays} days)</option>
                  <option value="after">After Today (Next {customDays} days)</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Bar */}
      <div style={{
        background: '#667eea',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '25px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '500' }}>
            <input 
              type="checkbox"
              checked={selectAll}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectAll(checked);
                if (checked) {
                  setSelectedIds(filteredFees.map(fee => fee._id));
                } else {
                  setSelectedIds([]);
                }
              }}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Select All ({filteredFees.length})
          </label>
          <div style={{ fontSize: '15px', fontWeight: '500' }}>
            Selected: {selectedIds.length}
          </div>
        </div>

        <button
          onClick={handleSendBulkReminders}
          disabled={selectedIds.length === 0 || sendingReminder}
          style={{
            padding: '12px 30px',
            background: selectedIds.length === 0 || sendingReminder ? '#cbd5e0' : '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: selectedIds.length === 0 || sendingReminder ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {sendingReminder ? '⏳ Sending...' : '📧 Send Reminders to Selected'}
        </button>
      </div>

      {/* Results Table */}
      {loading ? (
        <div style={{ 
          background: 'white',
          padding: '50px',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#718096',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          Loading...
        </div>
      ) : filteredFees.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '50px',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#718096',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          No pending fees found with the selected filters
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#4a5568', width: '50px' }}>
                  Select
                </th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Student
                </th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Contact
                </th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Pending Installments
                </th>
                <th style={{ padding: '15px', textAlign: 'right', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Pending Amount
                </th>
                <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredFees.map((fee) => {
                const pendingInst = getPendingInstallments(fee);
                const status = getInstallmentStatus(fee);

                return (
                  <tr 
                    key={fee._id}
                    style={{ 
                      borderBottom: '1px solid #e2e8f0',
                      background: selectedIds.includes(fee._id) ? '#f0f4ff' : 'white',
                      transition: 'background 0.2s'
                    }}
                  >
                    <td style={{ padding: '15px' }}>
                      <input 
                        type="checkbox"
                        checked={selectedIds.includes(fee._id)}
                        onChange={() => handleSelectToggle(fee._id)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ fontWeight: '600', color: '#1a202c', marginBottom: '3px' }}>
                        {fee.user?.name || 'N/A'}
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ fontSize: '13px', color: '#718096', marginBottom: '2px' }}>
                        {fee.user?.email || 'No email'}
                      </div>
                      {fee.user?.phone && (
                        <div style={{ fontSize: '13px', color: '#718096' }}>
                          📱 {fee.user.phone}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '15px' }}>
                      {pendingInst.length > 0 ? (
                        <div style={{ fontSize: '13px' }}>
                          {pendingInst.map(inst => {
                            const daysUntil = getDaysUntil(inst.dueDate);
                            return (
                              <div 
                                key={inst._id}
                                style={{ 
                                  marginBottom: '5px',
                                  padding: '5px 10px',
                                  background: daysUntil < 0 ? '#fed7d7' : '#fef3c7',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <span style={{ fontWeight: '500' }}>#{inst.installmentNumber}</span>
                                <span>{formatDate(inst.dueDate)}</span>
                                <span style={{ 
                                  color: daysUntil < 0 ? '#c53030' : '#92400e',
                                  fontWeight: '500',
                                  fontSize: '12px'
                                }}>
                                  {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Today' : `${daysUntil}d left`}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: '#718096' }}>None</span>
                      )}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#f56565' }}>
                        {formatCurrency(getFilteredPendingAmount(fee))}
                      </div>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: status.color + '20',
                        color: status.color
                      }}>
                        {status.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Footer */}
      {filteredFees.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px 20px',
          background: '#f7fafc',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '14px',
          color: '#4a5568'
        }}>
          <div>
            Showing {filteredFees.length} student(s) with pending installments
          </div>
          <div>
            Total Pending: <strong style={{ fontSize: '16px', color: '#f56565' }}>
              {formatCurrency(filteredFees.reduce((sum, fee) => sum + getFilteredPendingAmount(fee), 0))}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingFeesReminder;
